import axios from 'axios';
import AuthService from "./auth-service";
import { connectionMonitor, isPingUrl } from "./connection-monitor.js";

const NO_CONNECTION_DEBOUNCE_MS = 800;
let noConnectionTimer = null;
const AUTH_CONFLICT_PATHS = [
    '/api/auth/login',
    '/api/auth/refresh',
    '/api/auth/logout',
    '/api/token/refresh',
];

const scheduleNoConnection = (detail) => {
    if (noConnectionTimer) return;
    noConnectionTimer = setTimeout(() => {
        window.EventBus.dispatch('NO_CONNECTION', detail);
        noConnectionTimer = null;
    }, NO_CONNECTION_DEBOUNCE_MS);
};

const cancelNoConnection = () => {
    if (noConnectionTimer) {
        clearTimeout(noConnectionTimer);
        noConnectionTimer = null;
    }
};

// Il connectionMonitor è puramente diagnostico: se qualcosa va male (Sentry SDK,
// edge case di stato, browser API mancante) non deve mai propagare l'errore
// all'interceptor, altrimenti l'app perde la response/error reale.
const safeRecordSuccess = (config) => {
    try {
        connectionMonitor.recordSuccess(config);
    } catch {
        // diagnostica fallita, ignoriamo
    }
};

const safeRecordFailure = (error, config) => {
    try {
        connectionMonitor.recordFailure(error, config);
    } catch {
        // diagnostica fallita, ignoriamo
    }
};

export const JwtInterceptor = () => {
    // const isDevelopment = import.meta.env.MODE === 'development';
    axios.defaults.baseURL = window.location.origin;

    axios.interceptors.request.use(request => {
        const token = AuthService.token;
        const installId = AuthService.installId;

        request.headers.Accept = 'application/json';

        if (installId) {
            request.headers['X-Install-Id'] = installId;
        }

        if (token /*&& isApiUrl*/ && request.url !== '/api/auth/refresh') {
            request.headers.Authorization = `Bearer ${token}`;
        }

        return request;
    });

    axios.interceptors.response.use(
        response => {
            if (!isPingUrl(response.config?.url)) {
                safeRecordSuccess(response.config);
            }
            cancelNoConnection();
            return response;
        },
        error => {
            if (!error.response) {
                // Network error, CORS, DNS, or server unreachable
                if (!isPingUrl(error.config?.url)) {
                    safeRecordFailure(error, error.config);
                }
                scheduleNoConnection({
                    message: error.message,
                    url: error.config?.url
                });
                throw error;
            }

            switch (true) {
                case error.response?.status === 422: {
                    const violations = error.response.data?.violations || [];
                    const message = violations.map(violation => violation.message).join();

                    if (message) {
                        window.EventBus.dispatch('CONSTRAINT_VIOLATIONS', {message});
                    }
                    break;
                }
                case error.response?.status === 409:
                    if (error.response.data?.requiresConfirmation) {
                        break;
                    }

                    // TODO: sostituire il controllo sul path con un codice errore esplicito nel payload backend.
                    if (isAuthConflictPath(error.config?.url)) {
                        window.EventBus.dispatch('ALREADY_LOGGED_IN', {
                            message: error.response.data?.detail || 'Già loggato da un altro dispositivo'
                        });
                    }
                    break;
                case error.response?.status === 401: {
                    // distingue tra "sessione invalida" e "token scaduto"
                    const errorMessage = error.response?.data?.message || '';
                    if (errorMessage.includes('Session')) {
                        window.EventBus.dispatch('SESSION_INVALIDATED');
                    } else {
                        window.EventBus.dispatch('JWT_TOKEN_EXPIRED');
                    }
                    break;
                }
                case error.response?.status >= 500:
                    window.EventBus.dispatch('CRITICAL_ERROR', {
                        message: error.response.data?.detail || ''
                    });
                    break;
                case error.response?.status < 200:
                    window.EventBus.dispatch('NO_CONNECTION');
                    break;
                default:
                    break;
            }

            throw error;
        }
    );

};

function isAuthConflictPath(url) {
    return typeof url === 'string' && AUTH_CONFLICT_PATHS.some(path => url.includes(path));
}
